import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image, Download } from "lucide-react";
import { EditButton, DeleteButton } from "../components/common/ActionButtons";
import PageTitle from "../components/common/PageTitle";
import AddButton from "../components/common/AddButton";
import DataTable from "../components/common/DataTable";
import FilterDropdown from "../components/common/FilterDropdown";
import SearchBar from "../components/common/SearchBar";
import MobileFilterPanel from "../components/common/MobileFilterPanel";
import StatusToggle from "../components/common/StatusToggle";
import ExportExcelModal from "../components/modals/ExportExcelModal";
import {
  getLessons,
  getCategories,
  deleteLesson,
} from "../services/lessonService";
import { formatDateShort } from "../utils/dateFormat";
import { showError, showSuccess } from "../utils/toast";
import { confirmDelete, showConfirm } from "../utils/confirm";
import {
  PAGINATION,
  JWT,
  ROUTES,
  ROLES,
  LESSON_STATUS,
} from "../config/constants";
import { getProjectName } from "../utils/projectHelpers";
import { getCategoryName, getCategoryColor } from "../utils/categoryHelpers";
import { useCurrentUser } from "../hooks/useCurrentUser";
import {
  STORAGE_KEYS,
  CACHE_DURATION,
  saveToSessionStorage,
  getFromSessionStorage,
} from "../utils/sessionStorage";
import { API_BASE_URL } from "../api/apiClient";
import { Loader2 } from "lucide-react";
import ExportExcelButton from "../components/common/ExportExcelButton";


export default function LessonList() {
  const navigate = useNavigate();

  const [lessons, setLessons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get current user info
  const { role, division, isAdmin } = useCurrentUser();


const [pageLoading, setPageLoading] = useState(true);

useEffect(() => {
  const timer = setTimeout(() => {
    setPageLoading(false);
  }, 1200);

  return () => clearTimeout(timer);
}, []);

// ✅ role check aman (case insensitive)
const isProjectAdmin =
  role?.toLowerCase() === ROLES.PROJECT_ADMIN?.toLowerCase();

const isSuperAdmin =
  role?.toLowerCase() === ROLES.SUPER_ADMIN?.toLowerCase();

const canApprove = isProjectAdmin || isSuperAdmin;
  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Filter & Sort state
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchInput, setSearchInput] = useState(""); // For input field (immediate update)

  // Temporary filter state for mobile (not applied until user clicks "Cari")
  const [tempCategory, setTempCategory] = useState("");
  const [tempProject, setTempProject] = useState("");
  const [tempStatus, setTempStatus] = useState("");
  const [tempSort, setTempSort] = useState("newest");

  // Pagination state (backend pagination)
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLessons, setTotalLessons] = useState(0);
  const limit = PAGINATION.DEFAULT_PAGE_SIZE;

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    loadCategories();
    if (isAdmin) {
      loadProjects();
    }

    // Listen for window resize to detect mobile/desktop
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounce search: wait 500ms after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchKeyword(searchInput);
      setPage(1); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    loadLessons();
  }, [page, selectedCategory, selectedProject, selectedStatus, sortBy, searchKeyword]);

  async function loadCategories() {
    // Try to get from cache first
    const cachedCategories = getFromSessionStorage(
      STORAGE_KEYS.CATEGORIES,
      CACHE_DURATION.CATEGORIES,
    );

    if (cachedCategories) {
      setCategories(cachedCategories);
      return;
    }

    try {
      const data = await getCategories();
      setCategories(data);
      // Save to cache
      saveToSessionStorage(STORAGE_KEYS.CATEGORIES, data);
    } catch (err) {
      console.error("Failed to load categories:", err);
      showError("Gagal memuat kategori");
    }
  }


async function loadLessons() {
  setLoading(true);
  try {
    const params = {
      page,
      limit,
      sort_by: sortBy,
    };

    if (selectedCategory) params.category_id = selectedCategory;
    if (selectedProject) params.project_id = selectedProject;
    if (selectedStatus) params.status = selectedStatus;
    if (searchKeyword) params.search = searchKeyword;

    const data = await getLessons(params);

    setLessons(data.lessons || []);
    setTotalLessons(data.total || 0);
    setTotalPages(data.total_pages || 1);

  } catch (err) {
    console.error("Failed to load lessons:", err);

    if (err.response?.status === 401) {
      showError("Session expired. Silakan login kembali");
      localStorage.clear();
      navigate(ROUTES.LOGIN);
    } else {
      showError("Gagal memuat data lessons");
    }

    setLessons([]);
    setTotalLessons(0);
    setTotalPages(1);

  } finally {
    setLoading(false);
  }
}

async function loadProjects() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/project-names`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(JWT.TOKEN_KEY)}`,
      },
    });

    const data = await response.json();

    setProjects(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error("Failed to load projects:", err);
    showError("Gagal memuat daftar project");
  }
}

  async function handleDelete(id, caseTitle) {
    confirmDelete(`lesson "${caseTitle}"`, async () => {
      try {
        await deleteLesson(id);
        showSuccess("Lesson berhasil dihapus");
        loadLessons();
      } catch (err) {
        console.error("Failed to delete lesson:", err);

        if (err.response?.status === 403) {
          showError("Anda hanya bisa menghapus lesson yang Anda buat sendiri");
        } else {
          showError("Gagal menghapus lesson");
        }
      }
    });
  }

  function handleCategoryChange(categoryValue) {
    if (isMobile) {
      setTempCategory(categoryValue);
    } else {
      setSelectedCategory(categoryValue);
      setPage(1); // Reset to first page on filter change
    }
  }

  function handleProjectChange(projectValue) {
    if (isMobile) {
      setTempProject(projectValue);
    } else {
      setSelectedProject(projectValue);
      setPage(1); // Reset to first page on filter change
    }
  }

  function handleStatusChange(statusValue) {
    if (isMobile) {
      setTempStatus(statusValue);
    } else {
      setSelectedStatus(statusValue);
      setPage(1); // Reset to first page on filter change
    }
  }

  function handleSortChange(sortValue) {
    if (isMobile) {
      setTempSort(sortValue);
    } else {
      setSortBy(sortValue);
      setPage(1);
    }
  }

  function handleApplyFilters() {
    // Apply temporary filters to actual filters (mobile only)
    setSelectedCategory(tempCategory);
    setSelectedProject(tempProject);
    setSelectedStatus(tempStatus);
    setSortBy(tempSort);
    setPage(1);
  }

  function handleResetFilters() {
    setTempCategory("");
    setTempProject("");
    setTempStatus("");
    setTempSort("newest");
  }

  function handleSearchInput(e) {
    setSearchInput(e.target.value);
  }

  async function handleExport() {
    if (isAdmin) {
      // Show modal for admin
      setShowExportModal(true);
    } else {
      // Direct export for non-admin
      await exportLessons();
    }
  }

  async function exportLessons() {
    try {
      const token = localStorage.getItem(JWT.TOKEN_KEY);
      const response = await fetch(`${API_BASE_URL}/api/lessons/export/excel`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Export failed");
      }

      // Get filename from backend (dynamic: LessonLearned_{project}.xlsx)
      const disposition = response.headers.get("Content-Disposition");
      let filename = "LessonLearned_Export.xlsx"; // Fallback only
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      // Direct download (no blob preview)
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);

      showSuccess("Excel berhasil didownload");
    } catch (err) {
      console.error("Export error:", err);
      showError(err.message || "Gagal export data");
    }
  }

  function handleApprove(lessonId) {
    showConfirm({
      title: "Setujui Lesson?",
      message: "Lesson ini akan disetujui dan muncul di dashboard publik",
      confirmLabel: "Ya, Setujui",
      cancelLabel: "Batal",
      confirmColor: "blue",
      onConfirm: async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/lessons/${lessonId}/approve`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${localStorage.getItem(JWT.TOKEN_KEY)}`,
              },
            },
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to approve lesson");
          }

          showSuccess("Lesson berhasil disetujui");
          loadLessons(); // Reload list
        } catch (err) {
          console.error("Failed to approve lesson:", err);
          showError(err.message || "Gagal approve lesson");
        }
      },
    });
  }

  function handleUnapprove(lessonId, caseTitle) {
    showConfirm({
      title: "Unapprove Lesson?",
      message: `Lesson "${caseTitle}" akan dikembalikan ke status Pending.`,
      confirmLabel: "Ya, Unapprove",
      cancelLabel: "Batal",
      confirmColor: "red",
      onConfirm: async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/lessons/${lessonId}/unapprove`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${localStorage.getItem(JWT.TOKEN_KEY)}`,
              },
            },
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to unapprove lesson");
          }

          showSuccess("Lesson berhasil di-unapprove");
          loadLessons(); // Reload list
        } catch (err) {
          console.error("Failed to unapprove lesson:", err);
          showError(err.message || "Gagal unapprove lesson");
        }
      },
    });
  }

  const columns = [
    {
      field: "case_title",
      label: "Kasus",
      sortable: false,
      className: "min-w-[200px]",
    },
    {
      field: "causes",
      label: "Penyebab",
      sortable: false,
      className: "min-w-[150px]",
    },
    {
      field: "effect",
      label: "Dampak",
      sortable: false,
      className: "min-w-[150px]",
    },
    {
      field: "future_recommendation",
      label: "Rekomendasi",
      sortable: false,
      className: "min-w-[150px]",
    },
    {
      field: "project_action",
      label: "Tindakan",
      sortable: false,
      className: "min-w-[150px]",
    },
    {
      field: "project",
      label: "Project",
      sortable: false,
      className: "w-[150px]",
    },
    {
      field: "photos",
      label: "Foto",
      sortable: false,
      className: "w-[80px] text-center",
    },
    {
      field: "status",
      label: "Status",
      sortable: false,
      className: "w-[100px] text-center",
    },
  ];

  //loading animasi
  if (pageLoading) {
  return (
    <div className="fixed inset-0 z-[9999] bg-white overflow-hidden flex items-center justify-center">
      {/* Background Glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[-120px] left-[-80px] w-[380px] h-[380px] bg-cyan-200/40 rounded-full blur-3xl animate-pulse" />

        <div
          className="absolute bottom-[-120px] right-[-80px] w-[420px] h-[420px] bg-blue-200/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "6s" }}
        />

        <div
          className="absolute top-[40%] left-[45%] w-[260px] h-[260px] bg-sky-200/30 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "8s" }}
        />

        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      {/* Loader */}
      <div className="relative z-10 flex flex-col items-center">
        <div className="w-24 h-24 rounded-3xl bg-white/80 backdrop-blur-xl border border-gray-200 shadow-2xl flex items-center justify-center">
          <Loader2
            size={42}
            className="text-cyan-600 animate-spin"
          />
        </div>

        <p className="mt-6 text-sm text-gray-600 tracking-wide">
          Loading Platform...
        </p>
      </div>
    </div>
  );
}

  return (
    <div className="p-6 pt-20">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <PageTitle>Lessons Learned</PageTitle>
        <div className="relative z-30 flex gap-2">
          <ExportExcelButton
            onClick={handleExport}
          />
          <AddButton
            onClick={() => navigate(ROUTES.LESSONS_CREATE)}
            label="Tambah Lesson"
          />
        </div>
      </div>

      {/* MOBILE FILTER PANEL (reusable component) */}
      <MobileFilterPanel
        searchValue={searchInput}
        onSearchChange={handleSearchInput}
        searchPlaceholder="Cari lesson..."
        filters={[
          {
            label: "Kategori",
            options: [
              { value: "", label: "Semua Kategori" },
              ...categories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ],
            value: tempCategory,
            onChange: handleCategoryChange,
            placeholder: "Pilih Kategori",
          },
          {
            label: "Project",
            options: [
              { value: "", label: "Semua Project" },
              ...projects.map((proj) => ({
                value: proj.id,
                label: proj.name
              })),
            ],
            value: tempProject,
            onChange: handleProjectChange,
            placeholder: "Pilih Project",
            show: isAdmin,
          },
          {
            label: "Status",
            options: [
              { value: "", label: "Semua Status" },
              { value: LESSON_STATUS.PENDING, label: "Pending" },
              { value: LESSON_STATUS.APPROVED, label: "Approved" },
            ],
            value: tempStatus,
            onChange: handleStatusChange,
            placeholder: "Pilih Status",
          },
          {
            label: "Urutkan",
            options: [
              { value: "newest", label: "Terbaru" },
              { value: "oldest", label: "Terlama" },
            ],
            value: tempSort,
            onChange: handleSortChange,
            placeholder: "Pilih Urutan",
          },
        ]}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* DESKTOP FILTERS (Hidden on Mobile) */}
      <div className="hidden md:flex gap-4 mb-4">
        {/* Search */}
        <div className="flex-1">
          <SearchBar
            value={searchInput}
            onChange={handleSearchInput}
            placeholder="Cari berdasarkan judul, penyebab, dampak, tindakan, atau rekomendasi..."
          />
        </div>

        {/* Category Filter */}
        <div className="w-64">
          <FilterDropdown
            label="Kategori"
            options={[
              { value: "", label: "Semua Kategori" },
              ...categories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            value={selectedCategory}
            onChange={handleCategoryChange}
            placeholder="Pilih Kategori"
          />
        </div>

        {/* Project Filter (Admin Only) */}
        {isAdmin && (
          <div className="w-64">
            <FilterDropdown
              label="Project"
              options={[
                { value: "", label: "Semua Project" },
                ...(Array.isArray(projects) ? projects : []).map((proj) => ({
                  value: proj.id,
                  label: proj.name
                })),
              ]}
              value={selectedProject}
              onChange={handleProjectChange}
              placeholder="Pilih Project"
            />
          </div>
        )}

        {/* Status Filter */}
        <div className="w-48">
          <FilterDropdown
            label="Status"
            options={[
              { value: "", label: "Semua Status" },
              { value: LESSON_STATUS.PENDING, label: "Pending" },
              { value: LESSON_STATUS.APPROVED, label: "Approved" },
            ]}
            value={selectedStatus}
            onChange={handleStatusChange}
            placeholder="Pilih Status"
          />
        </div>

        {/* Sort */}
        <div className="w-48">
          <FilterDropdown
            label="Urutkan"
            options={[
              { value: "newest", label: "Terbaru" },
              { value: "oldest", label: "Terlama" },
            ]}
            value={sortBy}
            onChange={handleSortChange}
            placeholder="Pilih Urutan"
          />
        </div>
      </div>

      {/* INFO */}
      <div className="mb-4">
        <p className="text-gray-600">
          Menampilkan {lessons.length} dari {totalLessons} lessons
          {selectedCategory && (
            <span className="ml-2">
              (kategori:{" "}
              {getCategoryName(categories, parseInt(selectedCategory))})
            </span>
          )}
        </p>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={lessons}
        loading={loading}
        renderRow={(lesson, idx) => {
          const categoryColor = getCategoryColor(
            categories,
            lesson.category_id,
          );
          return (
            <tr
              key={lesson.id}
              className="hover:bg-gray-50 hover:cursor-pointer"
              onClick={() => navigate(ROUTES.LESSONS_DETAIL(lesson.id))}
            >
              <td className="px-6 py-4">
                <div className="flex flex-col gap-2">
                  {/* Judul Kasus */}
                  <span className="text-sm font-medium text-gray-900 leading-relaxed">
                    {lesson.case}
                  </span>

                  {/* Tanggal */}
                  <span className="text-xs text-gray-400">
                    {formatDateShort(lesson.created_at)}
                  </span>

                  {/* Kategori */}
                  <span
                    className="text-xs font-medium px-2 py-1 rounded-full w-fit"
                    style={{
                      backgroundColor: categoryColor,
                      color: "#FFFFFF",
                    }}
                  >
                    {getCategoryName(categories, lesson.category_id)}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="text-xs text-gray-600">
                  {lesson.causes}
                </span>
              </td>
                            <td className="px-6 py-4">
                <span className="text-xs text-gray-600">
                  {lesson.effect}
                </span>
              </td>
              
              <td className="px-6 py-4">
                <span className="text-xs text-gray-600">
                  {lesson.project_action}
                </span>
              </td>
                            <td className="px-6 py-4">
                <span className="text-xs text-gray-600">
                  {lesson.future_recommendation}
                </span>
              </td>
              <td className="px-6 py-4">
                <div>
                  <span className="text-xs text-gray-600">
                    {lesson.project_name ||
                      getProjectName(projects, lesson.project_id) ||
                      "-"}
                  </span>
                  {lesson.project_division && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {lesson.project_division}
                    </p>
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-center gap-1">
                  {lesson.photos && lesson.photos.length > 0 ? (
                    <>
                      {/* Tampilkan maksimal 2 foto */}
                  {lesson.photos.slice(0, 2).map((photo, photoIdx) => {
                    const url = `${API_BASE_URL}/${photo}`;
                    console.log("PHOTO URL:", url);

                    return (
                      <div
                        key={photoIdx}
                        className="w-10 h-10 rounded border border-gray-300 overflow-hidden bg-gray-100 flex-shrink-0"
                      >
                        <img
                          src={url}
                          alt={`Foto ${photoIdx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    );
                  })}
                      {/* Tampilkan badge +N jika ada lebih dari 2 foto */}
                      {lesson.photos.length > 2 && (
                        <div className="w-10 h-10 rounded border border-blue-300 bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-700">
                            +{lesson.photos.length - 2}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-1 text-gray-400">
                      <Image size={16} />
                      <span className="text-xs">No photo</span>
                    </div>
                  )}
                </div>
              </td>
              <td
                className="px-6 py-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col items-center gap-3">
                  {/* Status */}
                  <StatusToggle
                    status={lesson.status}
                    canEdit={canApprove}
                    onApprove={() => handleApprove(lesson.id)}
                    onUnapprove={() => handleUnapprove(lesson.id, lesson.case)}
                  />

                  {/* Actions */}
                  <div className="flex items-center justify-center gap-2">
                    <EditButton
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(ROUTES.LESSONS_EDIT(lesson.id));
                      }}
                      size={16}
                    />

                    <DeleteButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(lesson.id, lesson.case);
                      }}
                      size={16}
                    />
                  </div>
                </div>
              </td>
            </tr>
          );
        }}
        emptyMessage="Belum ada lesson. Klik tombol '+' untuk membuat lesson pertama."
        pagination={{
          page,
          totalPages,
          onPageChange: setPage,
        }}
      />

      {/* EXPORT MODAL (ADMIN ONLY) */}
      {isAdmin && (
        <ExportExcelModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          projects={projects}
        />
      )}
    </div>
  );
}
